import { cn } from "@/utils/cn"

export type LogoProps = {
  /** `mark` — icon only. `wordmark` — mark + "Seedmancer" side by side. */
  variant?: "mark" | "wordmark"
  alt?: string
  className?: string
}

/**
 * Inline SVG sprouting-seed mark.
 * Fixed SVG gradient IDs are safe here — all instances share identical colors.
 */
function SeedmancerMark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "relative isolate inline-flex aspect-square shrink-0 items-center justify-center overflow-hidden rounded-xl",
        "bg-[#020d06]",
        "shadow-[0_0_14px_-3px_rgba(52,211,153,0.22)]",
        className
      )}
      aria-hidden
    >
      {/* Ambient radial glow */}
      <span
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_55%_at_50%_20%,rgba(52,211,153,0.13),transparent_62%)]"
        aria-hidden
      />
      {/* Top specular sheen */}
      <span
        className="pointer-events-none absolute inset-x-[15%] top-px h-px rounded-full bg-gradient-to-r from-transparent via-emerald-300/25 to-transparent"
        aria-hidden
      />
      {/* Ring */}
      <span
        className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-white/[0.07]"
        aria-hidden
      />

      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="relative z-10 h-[62%] w-[62%] drop-shadow-[0_0_4px_rgba(52,211,153,0.25)]"
        aria-hidden
      >
        <defs>
          {/* Stem gradient — dark root → bright emerald → teal tip */}
          <linearGradient id="sm-g-stem" x1="12" y1="22" x2="12" y2="3" gradientUnits="userSpaceOnUse">
            <stop offset="0%"   stopColor="#065F46" />
            <stop offset="55%"  stopColor="#34D399" />
            <stop offset="100%" stopColor="#5EEAD4" />
          </linearGradient>
          {/* Left leaf — emerald, sweeps right-to-left */}
          <linearGradient id="sm-g-ll" x1="12" y1="14" x2="4" y2="11" gradientUnits="userSpaceOnUse">
            <stop stopColor="#34D399" stopOpacity="0.95" />
            <stop offset="1" stopColor="#059669" stopOpacity="0.75" />
          </linearGradient>
          {/* Right leaf — teal, sweeps left-to-right */}
          <linearGradient id="sm-g-lr" x1="12" y1="10" x2="20" y2="7" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2DD4BF" stopOpacity="0.95" />
            <stop offset="1" stopColor="#0F766E" stopOpacity="0.75" />
          </linearGradient>
        </defs>

        {/* ── Seed / root oval ── */}
        <ellipse
          cx="12" cy="21.4"
          rx="3" ry="1.8"
          fill="#064E3B"
          opacity="0.85"
        />

        {/* ── Stem ── */}
        <line
          x1="12" y1="20"
          x2="12" y2="5"
          stroke="url(#sm-g-stem)"
          strokeWidth="1.4"
          strokeLinecap="round"
        />

        {/*
          ── Left leaf ──
          Attaches to stem at y=18 (bottom) and y=14 (top).
          Tip sweeps LEFT to (4, 11).
        */}
        <path
          d="M12,18 C8,17 3,14 4,11 C5,8 11,10 12,14 Z"
          fill="url(#sm-g-ll)"
        />

        {/*
          ── Right leaf ──
          Attaches to stem at y=15 (bottom) and y=10 (top).
          Tip sweeps RIGHT to (20, 7).
        */}
        <path
          d="M12,15 C16,14 21,11 20,7 C19,4 14,5 12,10 Z"
          fill="url(#sm-g-lr)"
        />

        {/* ── Growing bud glow halo ── */}
        <circle cx="12" cy="4.5" r="3.2" fill="#34D399" opacity="0.18" />
        {/* ── Bud ── */}
        <circle cx="12" cy="4.5" r="1.7" fill="#A7F3D0" />
      </svg>
    </span>
  )
}

export function Logo({
  variant = "wordmark",
  alt = "Seedmancer",
  className,
}: LogoProps) {
  if (variant === "mark") {
    return (
      <span
        role="img"
        aria-label={alt}
        className={cn("inline-flex", className ?? "h-8 w-8")}
      >
        <SeedmancerMark className="h-full w-full" />
      </span>
    )
  }

  return (
    <span
      className={cn(
        "inline-flex max-w-full min-w-0 flex-nowrap items-center gap-2.5",
        className
      )}
    >
      <SeedmancerMark className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" />
      <span
        className={cn(
          "shrink-0 whitespace-nowrap font-semibold leading-none tracking-tight",
          "bg-gradient-to-r from-white via-gray-100 to-gray-400 bg-clip-text text-transparent",
          "text-[0.9375rem] sm:text-[1.0625rem]"
        )}
      >
        Seedmancer
      </span>
    </span>
  )
}
